/**
 * The citation grammar for agent responses: how sources are cited, and how raw-markdown
 * consumers read and replace citations.
 *
 * Current reference ids are `ref_` + 9 alphanumeric characters. Migrated chat
 * messages can contain 6-character ids with no reference rows; recognize those
 * tokens so they render as unresolved citations. The agent cites sources in two forms:
 *
 * 1. Reference marker — "[ref_ab12cdXYZ]": a bracketed id placed after the claim it
 *    supports. Renders as a numbered badge.
 * 2. Legal citation link — "[*Osborn v. Irwin*, 5 Cal. App. 4th 234 (1992)](ref_ab12cdXYZ)":
 *    a markdown link wrapping the authority text, targeting a bare citation id.
 *    Renders as the authority text itself, clickable.
 *
 * Token recognition matches the backend text parser
 * (paxton_app/agent_v2/citations/tokens.py). Reference validation uses the
 * current nine-character ID contract instead.
 */
const kCitationTokenIdPattern = 'ref_(?:[a-zA-Z0-9]{9}|[a-zA-Z0-9]{6})'
const kCurrentCitationIdPattern = 'ref_[a-zA-Z0-9]{9}'

/**
 * Authority text may contain bracketed runs one level deep — CommonMark link parsing
 * (which the chat renderer's structural detection follows) accepts balanced brackets, and
 * real Bluebook citations use them: NY-reporter years ("100 N.Y.2d 1 [2003]") and quote
 * alterations ("[sic]", "[T]he"). Non-capturing so composed regexes keep their groups.
 */
const kAuthorityTextPattern = '(?:[^\\[\\]\\n]|\\[[^\\[\\]\\n]*\\])+'

/** Reference marker in text, e.g. "[ref_ab12cdXYZ]". */
export const kCitationMarkerRegex = new RegExp(`\\[${kCitationTokenIdPattern}\\]`, 'g')

/** An <a> href that is exactly a bare citation id — a legal citation link after markdown parsing. */
export const kAgentLegalCitationLinkHrefRegex = new RegExp(`^${kCitationTokenIdPattern}$`)

/** Full-match test for a current reference id at data boundaries. */
export const kCitationIdRegex = new RegExp(`^${kCurrentCitationIdPattern}$`)

/**
 * Both forms in one alternation — the one tokenization every raw-markdown consumer shares
 * (mirrors the backend's _CITATION_RE). The link alternative comes first so a marker can
 * never match inside link syntax. Consumers never touch the capture groups directly:
 * read matches with readCitationMatch, or replace with replaceCitations.
 */
export const kCombinedCitationRegex = new RegExp(
  `\\[(?<authorityText>${kAuthorityTextPattern})\\]\\((?<linkId>${kCitationTokenIdPattern})\\)|\\[(?<markerId>${kCitationTokenIdPattern})\\]`,
  'g'
)

export type CitationToken = {
  citationId: string
  /** The authority text of a legal citation link; null for a reference marker. */
  authorityText: string | null
}

function tokenFromGroups(groups: Record<string, string | undefined> | undefined): CitationToken {
  if (groups?.linkId !== undefined) return { citationId: groups.linkId, authorityText: groups.authorityText ?? '' }
  return { citationId: groups?.markerId ?? '', authorityText: null }
}

/** Read a kCombinedCitationRegex match (e.g. from matchAll) as a citation token. */
export function readCitationMatch(match: RegExpMatchArray): CitationToken {
  return tokenFromGroups(match.groups)
}

/**
 * Replace every citation token (both forms) in document order. The grammar owns the
 * iteration so consumers never depend on the alternation's group layout.
 */
export function replaceCitations(text: string, replacer: (token: string, citation: CitationToken) => string): string {
  return text.replace(kCombinedCitationRegex, (token: string, ...rest: unknown[]) => {
    // With named groups present, String.replace passes the groups object as the final argument.
    const groups = rest[rest.length - 1] as Record<string, string | undefined>
    return replacer(token, tokenFromGroups(groups))
  })
}
